import s from './ViewOffer.module.sass';
import {
  Block,
  Button,
  Header,
  InlineHeader,
  List,
  ListItem,
  Placeholder,
  ScrollView,
} from '$uikit';
import React, { FC, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { offersActions, offersSelector } from '$store/offers';
import { useRoute } from 'react-router5';
import { Loader, Truncate, UnitLink, UnitName, UnitPhoto } from '$shared/components';
import {
  useCurrentUserInfo,
  useOffer,
  useTranslator,
  useUserInfo,
  useWikiLink,
} from '$hooks';
import { IcChevron16, IcDelete24, IcDelete28, IcEdit28 } from '$assets';
import { BundleAttachItem } from '$core/CreateBundle/BundleAttachItem/BundleAttachItem';
import copyToClipboard from 'copy-text-to-clipboard';
import { toastActions } from '$store/toast';
import { _confirm } from '$utils';
import { Api } from '$api';
import { goBack, showModal } from '$navigation/helper';
import { AttachItem, AuthPopoverState } from '$store/models';
import { authSelector } from '$store/auth';
import { profileActions } from '$store/profile';
import { AuthPopoverSimple } from '$core/AuthPopover/AuthPopover';

export const ViewOffer: FC = () => {
  const { route } = useRoute();
  const offerId = +route.params.id;
  const dispatch = useDispatch();
  const { offerLoadingStatus } = useSelector(offersSelector);
  const t = useTranslator();
  const offer = useOffer(offerId);
  const user = useUserInfo(offer?.userId ?? 0);
  const moreInfo = useWikiLink('offer');
  const curUser = useCurrentUserInfo();
  const { isLogged } = useSelector(authSelector);

  const loadData = () => {
    dispatch(offersActions.loadOffer(offerId));
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCopyLink = () => {
    if (!isLogged) {
      dispatch(profileActions.setAuthPopoverState(AuthPopoverState.LOGIN));
      return;
    }
    let path = `id${offer?.userId ?? 0}`;
    if (user && user.domain) {
      path = user.domain;
    }
    copyToClipboard(
      `https://ton.place/${path}?utm_source=offer_${offerId}&utm_campaign=${curUser?.id}`,
    );
    dispatch(toastActions.setToastSuccess(t('copied')));
  };

  const handleDelete = () => {
    _confirm(t('view_offer_delete_confirm'), () => {
      dispatch(toastActions.setToastLoading());
      Api.post(`/offers/${offerId}/delete`)
        .then(() => {
          dispatch(toastActions.setToastSuccess());
          goBack();
        })
        .catch((e) => {
          dispatch(toastActions.setToastFail(e.message));
        });
    });
  };

  const handleEdit = () => {
    showModal('edit_offer', false, { offer_id: offerId });
  };

  const handleContent = (item: AttachItem) => {
    if (item.type === 'photo') {
      window.open(item.photo?.photoLarge, '_blank');
    } else if (item.type === 'video') {
      try {
        const parsed = JSON.parse(item.video?.source ?? '');
        window.open(parsed[0].path, '_blank');
      } catch (e) {}
    }
  };

  function renderContent() {
    if (offerLoadingStatus === 'failed') {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={
            <Button size="small" onClick={loadData}>
              {t('failed_button')}
            </Button>
          }
          centered
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    if (offerLoadingStatus === 'loading' || !offer || !user) {
      return <Loader isWrapped />;
    }

    let period = 'Unknown';
    if (offer.sharePeriodDays === 7) {
      period = t('offer_create_period_7');
    } else if (offer.sharePeriodDays === 30) {
      period = t('offer_create_period_30');
    } else if (offer.sharePeriodDays === 90) {
      period = t('offer_create_period_90');
    } else if (offer.sharePeriodDays === 180) {
      period = t('offer_create_period_180');
    } else if (offer.sharePeriodDays === 365) {
      period = t('offer_create_period_365');
    }

    return (
      <>
        <UnitLink className={s.userInfo} unit={user}>
          <UnitPhoto unit={user} size="extra-small" round onClick={() => {}} />
          <div className={s.userName}>
            <UnitName unit={user} />
          </div>
          <div className={s.userChevron}>
            <IcChevron16 />
          </div>
        </UnitLink>
        <div className={s.description}>
          <Truncate
            lines={10}
            text={offer.description}
            expandText={t('post_text_expand')}
          />
        </div>
        <List>
          <ListItem indicator={`${offer.sharePercent}%`}>
            {t('view_offer_percent')}
          </ListItem>
          <ListItem indicator={period}>{t('view_offer_period')}</ListItem>
          <ListItem
            indicator={
              offer.shareType === 1
                ? t('offer_create_type_subscriptions')
                : t('offer_create_type_all')
            }
          >
            {t('view_offer_type')}
          </ListItem>
          <ListItem indicator={t('view_offer_source_any')}>
            {t('view_offer_source')}
          </ListItem>
        </List>
        {/*<a href={moreInfo} target="_blank" className={s.moreInfo} rel="noreferrer">*/}
        {/*  {t('view_offer_fields_wiki')}*/}
        {/*</a>*/}
        {offer.canShare && offer.userId !== curUser?.id && (
          <div className={s.link}>
            <Button onClick={handleCopyLink}>{t('view_offer_link_copy')}</Button>
            <div className={s.linkInfo}>{t('view_offer_link_info')}</div>
          </div>
        )}
        {!offer.canShare && offer.userId !== curUser?.id && (
          <Block className={s.cantShareBlock}>
            <div className={s.cantShareBlockInfo}>{t('view_offer_cant_share_text')}</div>
            <Button href="https://t.me/tonplacehelpbot" target="_blank">
              {t('view_offer_cant_share_btn')}
            </Button>
          </Block>
        )}
        <div className={s.contentTitle}>{t('view_offer_link_content')}</div>
        <div className={s.contentCaption}>{t('view_offer_link_content_caption')}</div>
        <div>
          {offer.content.length > 0 && (
            <div className="Bundle__content__grid">
              {offer.content.map((attach, index) => (
                <BundleAttachItem
                  decryptKey=""
                  key={index}
                  attach={attach}
                  onClick={() => {
                    handleContent(attach);
                  }}
                />
              ))}
            </div>
          )}
          {offer.content.length == 0 && (
            <Placeholder compact className={s.noContent}>
              {t('view_offer_link_content_empty')}
            </Placeholder>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <Header
        showBackButton
        rightIcons={[
          curUser?.id === offer?.userId
            ? {
                icon: <IcEdit28 />,
                onClick: handleEdit,
              }
            : null,
          curUser?.id === offer?.userId
            ? {
                icon: <IcDelete24 />,
                onClick: handleDelete,
              }
            : null,
        ]}
      >
        {t('view_offer_title')}
      </Header>
      <ScrollView>
        <div className={s.wrap}>{renderContent()}</div>
      </ScrollView>
      <AuthPopoverSimple />
    </>
  );
};
